/**
 * Screen 1 — "My uploads": the valuer's own recent uploads as a simple list, plus one
 * clear primary button. First-time/empty state shows a friendly prompt, never a blank page.
 */
import { useCallback, useEffect, useState } from 'react';
import { fetchMyUploads, fetchEta, type UploadListItem } from '../api';
import { Button, Tile, Spinner, Banner, StatusPill } from '../components/ui';
import { formatDate } from '../lib/status';
import { valuerEtaSentence, type EtaInfo } from '../lib/eta';

const STILL_PROCESSING = new Set(['uploaded', 'queued', 'checking']);

type State =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; items: UploadListItem[] };

export function MyUploads({ onNew, onViewFeedback }: { onNew: () => void; onViewFeedback: (reportId: string, address: string | null) => void }) {
  const [state, setState] = useState<State>({ status: 'loading' });
  const [eta, setEta] = useState<EtaInfo | null>(null);

  // Part IX: fetch the rolling estimate once anything is still processing.
  const anyProcessing = state.status === 'ready' && state.items.some((i) => STILL_PROCESSING.has(i.status));
  useEffect(() => {
    if (!anyProcessing) return;
    let cancelled = false;
    fetchEta()
      .then((info) => {
        if (!cancelled) setEta(info);
      })
      .catch(() => {
        if (!cancelled) setEta(null);
      });
    return () => {
      cancelled = true;
    };
  }, [anyProcessing]);

  const load = useCallback(async () => {
    setState({ status: 'loading' });
    try {
      const items = await fetchMyUploads();
      setState({ status: 'ready', items });
    } catch (err) {
      setState({ status: 'error', message: err instanceof Error ? err.message : 'Could not load your uploads.' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <main className="main">
      <div className="page-head">
        <h1 className="page-title">My uploads</h1>
        {state.status === 'ready' && state.items.length > 0 ? (
          <Button onClick={onNew}>Upload a new report</Button>
        ) : null}
      </div>

      {state.status === 'loading' ? (
        <Tile>
          <Spinner label="Loading your uploads…" />
        </Tile>
      ) : null}

      {state.status === 'error' ? (
        <div className="stack">
          <Banner tone="error">{state.message}</Banner>
          <div>
            <Button variant="secondary" onClick={() => void load()}>
              Try again
            </Button>
          </div>
        </div>
      ) : null}

      {state.status === 'ready' && state.items.length === 0 ? (
        <Tile className="tile--pad-lg">
          <div className="empty">
            <div className="empty__title">No uploads yet</div>
            <p className="empty__body">
              When you’re ready, upload a valuation report and its letter of instruction. We’ll take
              care of the review from there.
            </p>
            <Button onClick={onNew}>Upload a new report</Button>
          </div>
        </Tile>
      ) : null}

      {state.status === 'ready' && state.items.length > 0 ? (
        <Tile>
          <div className="uploads">
            {state.items.map((item) => (
              <div className="upload-row" key={item.id}>
                <div style={{ minWidth: 0 }}>
                  <div className="upload-row__addr" title={item.address ?? ''}>
                    {item.address || 'Address not recorded'}
                  </div>
                  <div className="upload-row__date">
                    {formatDate(item.date)}
                    {item.neededByAt ? <> · Needed by {formatDate(item.neededByAt)}</> : null}
                  </div>
                  {STILL_PROCESSING.has(item.status) && eta ? (
                    <div className="upload-row__date">{valuerEtaSentence(eta)}</div>
                  ) : null}
                </div>
                {item.status === 'changes_requested' ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
                    <StatusPill status={item.status} />
                    <Button onClick={() => onViewFeedback(item.id, item.address)}>View feedback</Button>
                  </div>
                ) : (
                  <StatusPill status={item.status} />
                )}
              </div>
            ))}
          </div>
        </Tile>
      ) : null}
    </main>
  );
}
